import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import merge from "lodash/merge";
import partition from "lodash/partition";
import isEmpty from "lodash/isEmpty";
import { Changes, InvestorProfileDetails } from "../types";
import { findPrimaryHolder, findSecondHolder } from "../utils";
import { investorProfileQueryOptions } from "./client/account";

import {
  getChangeRequestByClientId,
  createChangeRequest,
  acceptChangeRequest,
  rejectChangeRequest,
  notifyChangeRequest,
  ChangeSet,
} from "../api/change-request";

function calculateChanges(items: ChangeSet["items"]) {
  const [bankAccount, afterBank] = partition(
    items,
    (item) => item.target_model === "INVESTOR_BANK_ACCOUNT"
  );

  const [nomineeItems, others] = partition(
    afterBank,
    (item) => item.target_model === "NOMINEE"
  );

  const otherChanges = others.reduce(
    (acc, item) => {
      acc[item.target_model] = acc[item.target_model] || {};
      acc[item.target_model][item.field_name] = item.new_value;
      return acc;
    },
    {} as Record<keyof Changes, Record<string, unknown>>
  );

  const bankAccountChanges = bankAccount.reduce(
    (acc, c) => {
      acc[c.object_pk] = acc[c.object_pk] || {};
      if (!c.object_pk.startsWith("temp")) {
        acc[c.object_pk].bank_account_id = c.object_pk;
      }
      acc[c.object_pk][c.field_name] = c.new_value;
      return acc;
    },
    {} as Record<string, Record<string, unknown>>
  );

  const nomineeChanges = nomineeItems.reduce(
    (acc, c) => {
      acc[c.object_pk] = acc[c.object_pk] || {};
      if (!c.object_pk.startsWith("temp")) {
        acc[c.object_pk].nominee_id = c.object_pk;
      }
      acc[c.object_pk][c.field_name] = c.new_value;
      return acc;
    },
    {} as Record<string, Record<string, unknown>>
  );

  const changeResult: Changes = {
    ...(otherChanges as Omit<Changes, "INVESTOR_BANK_ACCOUNT" | "NOMINEE">),
  };

  if (!isEmpty(bankAccountChanges)) {
    changeResult.INVESTOR_BANK_ACCOUNT = Object.values(bankAccountChanges);
  }

  if (!isEmpty(nomineeChanges)) {
    changeResult.NOMINEE = Object.values(nomineeChanges);
  }

  return changeResult;
}

export function useInvalidateChangeSet(clientId: number) {
  const queryClient = useQueryClient();

  return useCallback(() => {
    return queryClient.invalidateQueries({
      queryKey: ["change-set", clientId],
    });
  }, [queryClient, clientId]);
}

export function useChangeSet(
  clientId: number,
  options?: { enabled?: boolean; profileId?: string }
) {
  const { data: changeRequest } = useQuery({
    queryKey: ["change-set", clientId],
    queryFn: () => getChangeRequestByClientId(clientId),
    enabled: options?.enabled,
  });

  const invalidateChangeSet = useInvalidateChangeSet(clientId);

  const { mutateAsync: addCr } = useMutation({
    mutationKey: [clientId],
    mutationFn: (data: Changes) =>
      createChangeRequest(clientId, data, options?.profileId),
  });

  // On joint accounts each holder has their own pending changes, so keep
  // account-level items (no profile) and only this holder's own
  const profileId = options?.profileId;
  const items = useMemo(
    () =>
      (changeRequest?.items || []).filter(
        (item) =>
          !profileId || !item.profile_id || item.profile_id === profileId
      ),
    [changeRequest?.items, profileId]
  );

  const changes = useMemo(() => calculateChanges(items), [items]);

  return [
    changes,
    useCallback(
      async (newChanges: Changes) => {
        const oldChanges = calculateChanges(items);
        const allChanges = merge({}, oldChanges, newChanges);
        const bankAccountChanges =
          newChanges.INVESTOR_BANK_ACCOUNT || oldChanges.INVESTOR_BANK_ACCOUNT;
        const nomineeChanges = newChanges.NOMINEE || oldChanges.NOMINEE;
        if (!isEmpty(bankAccountChanges)) {
          allChanges.INVESTOR_BANK_ACCOUNT = bankAccountChanges;
        }
        if (!isEmpty(nomineeChanges)) {
          allChanges.NOMINEE = nomineeChanges;
        }
        await addCr(allChanges);
        await invalidateChangeSet();
      },
      [items, addCr, invalidateChangeSet]
    ),
  ] as const;
}

// Always resolve profile ids: one per holder on joint accounts (optionally
// only those whose approval is pending), or the single holder's otherwise.
async function getProfileIds(
  queryClient: ReturnType<typeof useQueryClient>,
  clientId: number,
  onlyPending: boolean
) {
  const profiles = await queryClient
    .fetchQuery(investorProfileQueryOptions({ client_id: clientId }))
    .catch(() => undefined);
  const secondHolder = findSecondHolder(profiles);
  const toGuids = (holders: (InvestorProfileDetails | undefined)[]) =>
    holders
      .map((holder) => holder?.investor_profile_guid)
      .filter((guid): guid is string => !!guid);

  if (!secondHolder) return toGuids([findPrimaryHolder(profiles)]);
  const holders = [findPrimaryHolder(profiles), secondHolder];
  const pending = toGuids(
    holders.filter((holder) => !onlyPending || !holder?.is_approved)
  );
  return pending.length > 0 ? pending : toGuids(holders);
}

export function useNotifyChangeRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["notify-change-request"],
    mutationFn: async (clientId: number) => {
      const profileIds = await getProfileIds(queryClient, clientId, true);
      if (profileIds.length === 0) return notifyChangeRequest(clientId);
      const results = await Promise.all(
        profileIds.map((profileId) => notifyChangeRequest(clientId, profileId))
      );
      return results[0];
    },
  });
}

export function useAcceptChangeRequest() {
  return useMutation({
    mutationKey: ["accept-change-request"],
    mutationFn: acceptChangeRequest,
  });
}

export function useRejectChangeRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["reject-change-request"],
    // Rejecting is done against the primary holder only, even on joint accounts
    mutationFn: async (clientId: number) => {
      const profiles = await queryClient
        .fetchQuery(investorProfileQueryOptions({ client_id: clientId }))
        .catch(() => undefined);
      const primaryProfileId =
        findPrimaryHolder(profiles)?.investor_profile_guid;
      return rejectChangeRequest(clientId, primaryProfileId);
    },
  });
}

export const changeSetQueryOptions = (
  clientId: number,
  options?: { enabled?: boolean }
) =>
  queryOptions({
    queryKey: ["change-set", clientId],
    queryFn: () => getChangeRequestByClientId(clientId),
    enabled: options?.enabled,
  });

export function useChangeSetItems(
  clientId: number,
  options?: { enabled?: boolean }
) {
  const { data: changeRequest } = useQuery(
    changeSetQueryOptions(clientId, options)
  );
  return changeRequest?.items?.reduce(
    (acc, c) => {
      acc[c.target_model] = acc[c.target_model] || [];
      acc[c.target_model].push(c);
      return acc;
    },
    {} as Record<keyof Changes, ChangeSet["items"]>
  );
}
